import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";

// Self-contained "Units & Packs" editor.
// - Base (stock) unit = the smallest unit the item is counted/sold in.
// - Packs = bigger units you buy/sell in, each = N base units (unitConversions).
// Units are stored as MeasuringUnit ids, so renames in the master propagate.
export interface UnitsValue {
  baseUnit: string;
  baseUnitName: string;
  unitConversions: Record<string, number>;
}

interface Props {
  value?: { baseUnit?: string; unitConversions?: Record<string, number> };
  onChange: (val: UnitsValue) => void;
}

type Pack = { unitId: string; factor: string };

const UnitsPacksEditor: FC<Props> = ({ value, onChange }) => {
  const dispatch = useAppDispatch();
  const { units, byId } = useAppSelector((state) => state.units);

  const [baseUnit, setBaseUnit] = useState<string>("");
  const [multiUnit, setMultiUnit] = useState(false);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    console.log(units);
    if (!units || units.length === 0) {
      console.log("called useeffect and fetchunits", units.length );
      dispatch(fetchUnits());
    }
    console.log("Afterfetch", units);
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialise once from incoming value (edit mode). Only hydrate when the value
  // carries REAL data — a base unit or a non-empty unitConversions. The parent's
  // redux default is `{}` and it loads the SKU asynchronously, so an empty
  // placeholder ({ baseUnit: "", unitConversions: {} }) arrives first; since {}
  // is truthy, the old check locked `hydrated` before the real SKU landed and the
  // editor stayed blank on edit.
  useEffect(() => {
    if (hydrated) return;
    const hasBase = !!value?.baseUnit;
    const hasConversions = !!value?.unitConversions && Object.keys(value.unitConversions).length > 0;
    if (hasBase || hasConversions) {
      setBaseUnit(value!.baseUnit || "");
      const conv = value!.unitConversions || {};
      const rows = Object.keys(conv).map((unitId) => ({ unitId, factor: String(conv[unitId]) }));
      setPacks(rows);
      setMultiUnit(rows.length > 0);
      setHydrated(true);
    }
  }, [value, hydrated]);

  // Emit changes upward.
  useEffect(() => {
    const unitConversions: Record<string, number> = {};
    packs.forEach((p) => {
      const f = Number(p.factor);
      if (p.unitId && f > 0) unitConversions[p.unitId] = f;
    });
    onChange({
      baseUnit,
      baseUnitName: byId[baseUnit]?.name || byId[baseUnit]?.code || "",
      unitConversions: multiUnit ? unitConversions : {},
    });
  }, [baseUnit, packs, multiUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(
    () => (units || []).map((u: any) => ({ label: `${u.name}${u.code ? ` (${u.code})` : ""}`, value: u._id })),
    [units]
  );
  const baseLabel = byId[baseUnit]?.name || "base unit";

  const updatePack = (idx: number, patch: Partial<Pack>) =>
    setPacks((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const usedUnitIds = new Set([baseUnit, ...packs.map((p) => p.unitId)]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 1 }}>
      <Typography sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
        Units &amp; Packs
        <Tooltip title="Stock is counted in the base unit (the smallest unit you sell). Packs are bigger units you buy/sell in.">
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7a8290" }} />
        </Tooltip>
      </Typography>

      <Grid container spacing={2} sx={{ alignItems: "center" }}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={options}
            getOptionLabel={(o: any) => o.label || ""}
            isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
            value={options.find((o) => o.value === baseUnit) || null}
            onChange={(_e, v: any) => setBaseUnit(v ? v.value : "")}
            renderInput={(params) => (
              <TextField {...params} label="Stock (base) unit" size="small" helperText="Smallest unit you sell / count" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Switch checked={multiUnit} onChange={(e) => setMultiUnit(e.target.checked)} />}
            label="I buy / sell this in bigger packs"
          />
        </Grid>
      </Grid>

      {multiUnit && (
        <Box sx={{ mt: 1 }}>
          {packs.map((row, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1, alignItems: "center" }}>
              <Grid item xs={5} sm={4}>
                <Autocomplete
                  options={options.filter((o) => !usedUnitIds.has(o.value) || o.value === row.unitId)}
                  getOptionLabel={(o: any) => o.label || ""}
                  isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
                  value={options.find((o) => o.value === row.unitId) || null}
                  onChange={(_e, v: any) => updatePack(idx, { unitId: v ? v.value : "" })}
                  renderInput={(params) => <TextField {...params} label="Pack unit" size="small" />}
                />
              </Grid>
              <Grid item xs={1} sx={{ textAlign: "center" }}>
                =
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={`How many ${baseLabel}`}
                  value={row.factor}
                  onChange={(e) => updatePack(idx, { factor: e.target.value })}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <IconButton size="small" onClick={() => setPacks((rows) => rows.filter((_, i) => i !== idx))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => setPacks((rows) => [...rows, { unitId: "", factor: "" }])}
            sx={{ color: "#488dff", backgroundColor: "#fff", border: "0.5px solid #488dff", borderRadius: "7px", textTransform: "none", "&:hover": { backgroundColor: "#f5f9ff", border: "0.5px solid #488dff" } }}>
            Add pack
          </Button>

          {/* live preview — each pack also expressed via the next-smaller pack */}
          {packs.some((p) => p.unitId && Number(p.factor) > 0) && (
            <Box sx={{ mt: 1, color: "#5b6472", fontSize: 13 }}>
              {(() => {
                const valid = packs
                  .filter((p) => p.unitId && Number(p.factor) > 0)
                  .map((p) => ({ name: byId[p.unitId]?.name || "?", factor: Number(p.factor) }))
                  .sort((a, b) => a.factor - b.factor);
                return valid
                  .map((p, i) => {
                    const smaller = i > 0 ? valid[i - 1] : null;
                    if (smaller && p.factor > smaller.factor) {
                      const ratio = p.factor / smaller.factor;
                      const ratioStr = Number.isInteger(ratio) ? String(ratio) : ratio.toFixed(2).replace(/\.?0+$/, "");
                      return `1 ${p.name} = ${ratioStr} ${smaller.name} = ${p.factor} ${baseLabel}`;
                    }
                    return `1 ${p.name} = ${p.factor} ${baseLabel}`;
                  })
                  .join("  ·  ");
              })()}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default UnitsPacksEditor;
